import React, { useMemo, useState } from "react";
import { Box, Skeleton, Accordion } from "@mantine/core";
import Pagination from "@/components/molecules/Pagination";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  Row,
} from "@tanstack/react-table";
import { useTablePagination } from "@/hooks/useTablePagination";
import styles from "./styles.module.css";
import tablestyles from "./tablestyles.module.css";
import Group from "@/components/atoms/Group";
import { classNames } from "@/utils/Classnames";

import { useTranslation } from "react-i18next";
import Table from "@/components/atoms/Table";

interface Props<TData extends object> {
  /** Array of data to display in the table */
  data: TData[];
  /** Column definitions for the table structure */
  columns: ColumnDef<TData>[];
  /** Whether the table is in a loading state - displays skeleton loaders when true */
  isLoading: boolean;
  /** Whether to show client-side pagination */
  showPagination?: boolean;
  /** The index (or indices) of the column(s) to display in the mobile accordion trigger */
  mobileHeaderCell?: number | number[];
  /** Ids of columns left out of the expanded mobile panel, e.g. a cell that already says everything in the collapsed header */
  mobilePanelOmitColumns?: string[];
  /** Action when clicking a row*/
  onRowClick?: (row: Row<any>) => void;
  /** Action when mouse enters a row — useful for prefetching detail data */
  onRowMouseEnter?: (row: Row<any>) => void;
  /** Column ID whose cell should suppress the onRowMouseEnter prefetch callback */
  skipPrefetchForColumn?: string;
  /** Optional rows per page for client-side pagination */
  initialSort?: SortingState;
  /** Optional rows per page for client-side pagination */
  initialRowsPerPage?: number;
  /** Is this table actionable? if is it will not display skeletons in this field **/
  actionable?: boolean;

  /** Whether to use server-side pagination */
  useServerSidePagination?: boolean;
  /** Controlled active page. If provided with onPageChange, the component becomes controlled */
  activePage?: number;
  /** Callback when page changes. Required if activePage is provided */
  onPageChange?: (page: number) => void;
  /** Controlled rows per page. If provided with onRowsPerPageChange, the component becomes controlled */
  rowsPerPage?: number;
  /** Callback when rows per page changes. Required if rowsPerPage is provided */
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  /** Total items in the table */
  totalItems?: number;
  /** Optional summary row (a `Table.Tr`) rendered in the table footer, e.g. a sum. Desktop table only */
  footerRow?: React.ReactNode;

  rowClassName?: (row: TData) => string;
  /** Prefix used to build data-testid attributes for the pagination controls */
  testIdPrefix?: string;
}

/**
 * A reusable data table component with sorting, pagination, and optional row actions.
 * @template TData - The type of data objects to display in the table
 * @param props - Component props
 * @param props.data - Array of data to display in the table
 * @param props.columns - Column definitions for the table structure
 * @param props.isLoading - Whether the table is in a loading state - displays skeleton loaders when true
 * @param props.showPagination - Whether to show client-side pagination
 * @param props.mobileHeaderCell - The index of the column to display in the mobile accordion view
 * @param props.mobilePanelOmitColumns - Ids of columns to leave out of the expanded mobile panel
 * @param props.initialSort - Pass a initial sorting state to the table
 * @param props.initialRowsPerPage- Pass a initial amount of rows per page to the table
 * @param props.useServerSidePagination - Whether to use server-side pagination
 * @param props.activePage - Controlled active page. If provided with onPageChange, the component becomes controlled
 * @param props.onPageChange - Callback when page changes. Required if activePage is provided
 * @param props.rowsPerPage - Controlled rows per page. If provided with onRowsPerPageChange, the component becomes controlled
 * @param props.onRowsPerPageChange - Callback when rows per page changes. Required if rowsPerPage is provided
 * @param props.totalItems - Total items in the table
 * @param props.rowClassName - class to apply to each row
 * - For media breakpoints - https://mantine.dev/styles/responsive/#hidden-and-visible-from-as-classes
 */
const DataTable = <TData extends object>({
  data,
  columns,
  isLoading,
  showPagination = true,
  mobileHeaderCell = 0,
  mobilePanelOmitColumns,
  onRowClick,
  onRowMouseEnter,
  skipPrefetchForColumn,
  initialSort = [],
  initialRowsPerPage = 15,
  actionable = true,
  activePage: controlledActivePage,
  onPageChange,
  rowsPerPage: controlledRowsPerPage,
  onRowsPerPageChange,
  totalItems,
  useServerSidePagination = false,
  footerRow,
  rowClassName,
  testIdPrefix,
}: Props<TData>) => {
  const [sorting, setSorting] = useState<SortingState>(initialSort);
  const [internalRowsPerPage, setInternalRowsPerPage] = useState(initialRowsPerPage);
  const [internalActivePage, setInternalActivePage] = useState(1);

  const { t } = useTranslation();

  const activePage = controlledActivePage ?? internalActivePage;
  const rowsPerPage = controlledRowsPerPage ?? internalRowsPerPage;

  const handlePageChange = (page: number) => {
    if (onPageChange) {
      onPageChange(page);
    } else {
      setInternalActivePage(page);
    }
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    if (onRowsPerPageChange) {
      onRowsPerPageChange(newRowsPerPage);
    } else {
      setInternalRowsPerPage(newRowsPerPage);
    }
  };

  const tableData = React.useMemo(
    () => (isLoading ? Array(Number(rowsPerPage)).fill({}) : data),
    [isLoading, data, rowsPerPage]
  );

  const tableColumns = useMemo(() => {
    const cols = [...columns];
    if (isLoading) {
      return columns.map((col) => ({
        ...col,
        cell: () => <Skeleton p={0} height={30} width={"100%"} />,
      }));
    }
    return cols;
  }, [columns, isLoading]);

  const { paginatedData } = useTablePagination({
    data: tableData || [],
    activePage,
    rowsPerPage,
  });

  // When using server-side pagination, use data directly; otherwise use paginated data
  const displayData = useServerSidePagination ? tableData : paginatedData;
  const table = useReactTable({
    data: displayData ?? ([] as TData[]),
    columns: tableColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    defaultColumn: {
      size: undefined,
      minSize: undefined,
      maxSize: undefined,
    },
  });

  return (
    <Box>
      <Table
        className={styles.dataTableRoot}
        withTableBorder
        withRowBorders
        classNames={{ thead: tablestyles.thead, th: tablestyles.th }}
      >
        <Table.Thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.Tr key={headerGroup.id}>
              {headerGroup.headers.map((header, idx) => {
                //@ts-expect-error has to be ignored since use of meta break
                const breakpoint = header.column.columnDef.meta?.breakpoint;
                const breakpointClass = breakpoint ? `${breakpoint}` : "";
                return (
                  <Table.Th
                    key={`${header.id} ${idx}`}
                    colSpan={header.colSpan}
                    className={breakpointClass}
                    style={{
                      width: header.column.columnDef.size !== undefined ? header.getSize() : undefined,
                    }}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </Table.Th>
                );
              })}
            </Table.Tr>
          ))}
        </Table.Thead>
        <Table.Tbody>
          {table.getRowModel().rows.map((row, index) => {
            const rowClassNames = rowClassName ? rowClassName(row.original) : "";
            return (
              <Table.Tr
                key={`${row.id} ${index}`}
                onClick={() => onRowClick && onRowClick(row)}
                onMouseEnter={
                  onRowMouseEnter
                    ? (e) => {
                        if (!(e.target as HTMLElement).closest("[data-no-prefetch]")) {
                          onRowMouseEnter(row);
                        }
                      }
                    : undefined
                }
                className={classNames(onRowClick ? "clickable" : "", rowClassNames)}
              >
                {row.getVisibleCells().map((cell, cellIndex) => {
                  const cells = row.getVisibleCells();
                  const shouldHideSkeleton = actionable
                    ? isLoading && (cellIndex === 0 || cellIndex === cells.length - 1)
                    : false;

                  //@ts-expect-error Has to be ignored since use of meta break
                  const breakpoint = cell.column.columnDef.meta?.breakpoint;
                  const breakpointClass = breakpoint ? `${breakpoint}` : "";
                  return (
                    <Table.Td
                      key={`${cell.id} ${cellIndex}`}
                      style={{
                        visibility: shouldHideSkeleton ? "hidden" : "visible",
                      }}
                      className={breakpointClass}
                      data-no-prefetch={
                        skipPrefetchForColumn && cell.column.id === skipPrefetchForColumn ? true : undefined
                      }
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Table.Td>
                  );
                })}
              </Table.Tr>
            );
          })}
        </Table.Tbody>
        {footerRow && <Table.Tfoot>{footerRow}</Table.Tfoot>}
      </Table>
      {/* Mobile Accordion table*/}
      <Accordion
        className={styles.mobileAccordions}
        classNames={{ label: styles.accordionLabel }}
        defaultValue="item-0"
        unstyled={true}
      >
        {table.getRowModel().rows.length === 0 && (
          <Accordion.Item value="item-0" className={styles.accordionItem}>
            <Accordion.Control className={styles.accordionControl}>
              {t("notifications:generic.noRows")}
            </Accordion.Control>
          </Accordion.Item>
        )}
        {table.getRowModel().rows.map((row, i) => {
          const triggerCellIndices: number[] = Array.isArray(mobileHeaderCell) ? mobileHeaderCell : [mobileHeaderCell];
          const triggerCells = triggerCellIndices.map((index: number) => row.getVisibleCells()[index]);
          return (
            <Accordion.Item key={row.id} value={`item-${i}`} className={styles.accordionItem}>
              <Accordion.Control className={styles.accordionControl}>
                <div className={styles.accordionTrigger}>
                  {triggerCells.map((triggerCell) => (
                    <span key={triggerCell.id}>
                      {flexRender(triggerCell.column.columnDef.cell, triggerCell.getContext())}
                    </span>
                  ))}
                </div>
              </Accordion.Control>
              <Accordion.Panel className={styles.accordionContent}>
                <ul className={styles.mobileTableRow}>
                  {row.getVisibleCells().map((cell, j) => {
                    const header = table.getHeaderGroups()[0].headers.find((h) => h.id === cell.column.id);
                    if (header?.column.id === "actions") return null;
                    if (mobilePanelOmitColumns?.includes(cell.column.id)) return null;
                    return (
                      <li key={j} className={styles.mobileTableCell}>
                        <Group justify="space-between">
                          {header && flexRender(header.column.columnDef.header, header.getContext())}
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </Group>
                      </li>
                    );
                  })}
                </ul>
              </Accordion.Panel>
            </Accordion.Item>
          );
        })}
      </Accordion>
      {showPagination && (
        <Pagination
          activePage={activePage}
          totalItems={totalItems ?? data?.length ?? 0}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(value) => handleRowsPerPageChange(Number(value))}
          onPageChange={handlePageChange}
          testIdPrefix={testIdPrefix}
        />
      )}
    </Box>
  );
};

export default DataTable;
